import type { Ref } from 'vue'
import { getSocialiteApiService } from '@simploshop-services/Socialite.service'
import { joinURL } from 'ufo'
import { ME_MODEL_EMBEDS } from '../assets/ts/constants/customer'
import { AUTH_RETURN_URL_COOKIE_NAME, CART_TRANSFER_SOURCE_COOKIE_NAME } from '../../shared/constants'

export function useAuth() {
    const store = useAuthStore()
    const { customer: storeCustomer, _meModel: storeMe, isLoggedIn: storeIsLoggedIn } = storeToRefs(store)
    const { $i18n } = useNuxtApp()
    const events = useEvents()

    const appConfig = useAppConfig()
    const { notifyError } = useNotifications()

    // @ts-expect-error Pinia doesn't pass instance types correctly
    const syncedMe: Ref<InstanceType<typeof MeModel> | null> = ref(storeMe.value)
    // @ts-expect-error Pinia doesn't pass instance types correctly
    const syncedCustomer: Ref<InstanceType<typeof CustomerModel> | null> = ref(storeCustomer.value)
    const syncedIsLoggedIn: Ref<boolean> = ref(storeIsLoggedIn.value)

    let wasSyncedStoreWatcherInitialized = false

    function initializeSyncedStoreWatcher() {
        if (wasSyncedStoreWatcherInitialized) return

        if (!getCurrentScope()) {
            throw new Error('[useAuth]: The synced properties must be accessed in the top-level of a component\'s script setup block or other places where Nuxt context is available.')
        }

        const callOnPageLoaded = useCallOnPageLoaded()

        const watcher = watch(storeMe, (val) => {
            callOnPageLoaded(() => {
                // @ts-expect-error Pinia doesn't pass instance types correctly
                syncedMe.value = storeMe.value
                syncedCustomer.value = storeCustomer.value
                syncedIsLoggedIn.value = storeIsLoggedIn.value
            })
        })

        onScopeDispose(() => watcher.stop())

        wasSyncedStoreWatcherInitialized = true
    }

    let passwordResetToken: ComputedRef<string | null>

    /**
     * Log in the customer with the provided credentials.
     *
     * In case the user has permission for impersonating other customers,
     * a `customerId` can be provided instead of email and password.
     * @param credentials The email and password of the customer
     * @throws FetchError if the login request fails
     * @todo Change FetchError to ApiResponseError
     */
    async function logIn(credentials: { email: string, password: string } | { customerId: number }) {
        // get the cookie with the auth token etc.
        const isImpersonating = 'customerId' in credentials
        if (isImpersonating) {
            await $fetch('/api/auth/impersonate', {
                method: 'POST',
                body: {
                    customerId: credentials.customerId,
                },
            })
        } else {
            await $fetch('/api/auth/login', {
                method: 'POST',
                body: {
                    email: credentials.email,
                    password: credentials.password,
                },
            })
        }

        // fetch me
        const meResponse = await getMeApiService()
            .embed([
                ...ME_MODEL_EMBEDS,
                [MeModel.EMBED_CUSTOMER, {
                    // @ts-ignore - TODO: fix app config type
                    embed: (appConfig.embeds?.customer ?? []),
                }],
            ])
            .exceptAttrs([MeModel.ATTR_CUSTOMER])
            .get()
        const me = meResponse.getItem()

        // if for some reason the 'Me' model could not be fetched, notify the user & return
        // the cookie should be set at this point so a simple page refresh should fix the issue
        if (!me) {
            notifyError($i18n.t('_core_simploshop.errors.login_failed'))
            return
        }

        store.loggingInMeModel = me
        // await all the logging-in callbacks
        await events.emit('customer:logging-in', { me, isImpersonating })

        // visually log in the customer
        store._setCustomer(me)

        // emit the logged-in event to indicate that the login has been fully completed
        events.emit('customer:logged-in', { me, isImpersonating })
            .then(() => {
                store.loggingInMeModel = null
            })
    }

    /**
     * Register a new customer.
     * @param payload The customer's registration data
     * @throws ApiResponseError if the registration request fails
     */
    async function register(payload: {
        firstName: string
        lastName: string
        email: string
        password: string
        passwordConfirmation: string
        shouldSubscribeToNewsletter: boolean
        birthAt?: string | null
        dialCode?: string | null
        phone?: string | null
    }) {
        await getCustomersApiService()
            .registerCustomer({
                first_name: payload.firstName,
                last_name: payload.lastName,
                email: payload.email,
                password: payload.password,
                password_confirmation: payload.passwordConfirmation,
                is_subscribed: payload.shouldSubscribeToNewsletter,
                birth_at: payload.birthAt ?? undefined,
                dial_code: payload.dialCode ?? undefined,
                phone: payload.phone ?? undefined,
            })

        // await all the registering callbacks
        await events.emit('customer:registering', { credentials: { email: payload.email, password: payload.password } })

        // emit the registered event to indicate that the registration has been fully completed
        events.emit('customer:registered', { credentials: { email: payload.email, password: payload.password } })
    }

    /**
     * Log out the currently logged-in customer.
     * If no customer is not logged in, this function does nothing.
     *
     * Automatically notifies the user if the logout request fails.
     */
    async function logOut() {
        if (!storeIsLoggedIn.value) return

        try {
            await $fetch('/api/auth/logout', {
                method: 'POST',
            })

            store.isLoggingOut = true
            // await all the logging-out callbacks
            await events.emit('customer:logging-out', {})

            // visually log out the customer
            store.removeCustomer()

            // emit the logged-out event to indicate that the logout has been fully completed
            events.emit('customer:logged-out', {})
                .then(() => {
                    store.isLoggingOut = false
                })
        } catch (e) {
            console.error(e)

            notifyError()
        }
    }

    /**
     * Send password reset token for a customer account with the given email
     * @throws ApiResponseError if the send reset token request fails
     * @param payload the email of the customer account for which to reset the password
     */
    async function sendPasswordResetToken(payload: { email: string }) {
        await getCustomerPasswordsApiService().post({
            email: payload.email,
        })
    }

    /**
     * Reset the password of a customer with the given password reset token
     * @throws ApiResponseError if the reset password request fails
     * @param payload the password reset data
     */
    async function resetPassword(payload: { token: string, new_password: string, new_password_confirmation: string,  }) {
        await getCustomerPasswordsApiService().put({
            token: payload.token,
            new_password: payload.new_password,
            new_password_confirmation: payload.new_password_confirmation,
        })
    }

    /**
     * Update the password of a customer. Provide the old password and the new password with confirmation.
     * @throws ApiResponseError if the update password request fails
     * @param payload
     */
    async function changePassword(payload: { password_old: string, password: string, password_confirmation: string }) {
        await getCustomersPasswordsApiService({
            customerId: storeCustomer.value?.id,
        }).put(payload)
    }

    async function deleteAccount() {
        await getCustomersApiService().forId(storeCustomer.value?.id).delete()
        store.removeCustomer()
    }

    return {
        me: storeMe,
        customer: storeCustomer,
        isLoggedIn: storeIsLoggedIn,
        logIn: logIn,
        logOut: logOut,
        register: register,
        sendPasswordResetToken: sendPasswordResetToken,
        resetPassword: resetPassword,
        changePassword: changePassword,
        deleteAccount: deleteAccount,
        updateCustomer: store.updateCustomer,

        get passwordResetToken() {
            if (!passwordResetToken) {
                passwordResetToken = computed(() => {
                    const route = useRoute()
                    return route.query.token ? (route.query.token as string) : null
                })
            }

            return passwordResetToken
        },

        syncedMe: lazyInit(syncedMe, initializeSyncedStoreWatcher),
        syncedCustomer: lazyInit(syncedCustomer, initializeSyncedStoreWatcher),
        syncedLoggedIn: lazyInit(syncedIsLoggedIn, initializeSyncedStoreWatcher),
    }
}

export function useThirdPartyLogin() {
    const thirdPartyAuthReturnUrlBase = useFullUrl('/api/auth/third-party/')
    const route = useRoute()
    const thirdPartyReturnUrl = useCookie(AUTH_RETURN_URL_COOKIE_NAME, {
        maxAge: 60 * 15, // 15 minutes
    })
    const cartTransferSourceId = useCartTransferSourceIdCookie()

    const { notifyError } = useNotifications()
    const { $i18n } = useNuxtApp()
    const cartStore = useCartStore()

    async function loginThirdParty(provider: SocialiteTypeEnum) {
        try {
            const response = await getSocialiteApiService()
                .getRedirectUrl({
                    provider: provider,
                    // Nuxt redirect handler
                    return_url: joinURL(thirdPartyAuthReturnUrlBase, provider),
                })

            const redirectUrl = response.getItem()?.providerAuthUrl

            if (!redirectUrl) {
                console.error('[auth:loginThirdParty]: No redirect URL returned.', response.getItem())
                notifyError($i18n.t('_core_simploshop.errors.invalid_return_url'))
                return
            }

            // save the return URL in a cookie
            thirdPartyReturnUrl.value = route.fullPath
            // save the current cart id in a cookie to transfer it to the new cart once we come back
            if (cartStore.cart?.id && cartStore.cart.uniqueAmount) {
                cartTransferSourceId.value = cartStore.cart.id
            } else {
                console.error('[auth:loginThirdParty]: No cart id found to transfer.')
            }

            await navigateTo(redirectUrl, { external: true })
        } catch (e) {
            let errorMessage: string | null = null
            if (e instanceof ApiResponseError) {
                errorMessage = e.getFirstValidationError().message ?? e.getErrorMessage()
            }

            notifyError(errorMessage || $i18n.t('_core_simploshop.labels.unknown_error'))
        }
    }

    return {
        loginThirdParty,
    }
}

export function useCartTransferSourceIdCookie() {
    return useCookie(CART_TRANSFER_SOURCE_COOKIE_NAME, {
        maxAge: 60 * 15, // 15 minutes
    })
}
